import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Field, inputClass } from '../../../../../components/ui/Field';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { unpickLine } from '../../../../../features/fulfillment/fulfillmentThunks';
import { unpickProblem } from '../../../../../features/fulfillment/fulfillmentUtils';
import { slotOptionLabel } from '../../../../../features/slots/slotsUtils';
import { formatQuantity } from '../../../../../utils/inventoryLabels';

/**
 * Puts picked units of one line back on a shelf.
 *
 * The units are on the packing table, so the question is where they go back
 * to: any active storage or picking location of the warehouse. The server
 * writes the reversal in the kardex and reactivates the reservation.
 *
 * @param {object} props
 * @param {object|null} props.line - The picklist line; `null` keeps it closed.
 * @param {number} props.orderId
 * @param {Array<object>} props.destinations - Active storage and picking locations.
 * @param {() => void} props.onClose
 * @param {(notice: {type: string, message: string}) => void} props.onDone
 */
export const UnpickLineModal = ({ line, orderId, destinations, onClose, onDone }) => {
  const dispatch = useDispatch();
  const isSaving = useSelector((state) => state.fulfillment.busyLineId === line?.order_product_id);

  const [slotId, setSlotId] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [lot, setLot] = useState('');
  const [reason, setReason] = useState('');
  const [saveError, setSaveError] = useState(null);

  const lineId = line?.order_product_id;

  // Every opening starts on everything picked and, when there is one, on the
  // shelf the server would have sent the picker to.
  useEffect(() => {
    if (!line) return;
    setSlotId(line.suggestions?.[0]?.slot_id ?? null);
    setQuantity(String(line.prepared_quantity || 0));
    setLot('');
    setReason('');
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  if (!line) return null;

  const max = Number(line.prepared_quantity || 0);
  const problem = unpickProblem({ slotId, quantity, max });
  const destination = destinations.find((slot) => slot.id === slotId) || null;

  const options = destinations.map((slot) => ({
    value: slot.id,
    label: slotOptionLabel(slot),
    shortLabel: slot.name,
  }));

  const handleSave = async () => {
    if (problem || isSaving) return;
    setSaveError(null);

    const action = await dispatch(unpickLine({ orderId, line, slotId, quantity, lot, reason }));

    if (action.meta.requestStatus !== 'fulfilled') {
      setSaveError(action.payload);
      return;
    }
    onDone({
      type: 'success',
      message: `${formatQuantity(quantity)} un de ${line.sku} devueltas a ${destination?.name || 'la ubicación'}`,
    });
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} title={`Deshacer picking de ${line.sku}`} testId="unpick-modal">
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {line.product_name} · {formatQuantity(max)} {line.base_uom || 'un'} en la mesa de preparación
      </p>

      <div className="space-y-4">
        {saveError && (
          <div
            data-testid="unpick-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {saveError}
          </div>
        )}

        <Field label={`Cantidad (máximo ${formatQuantity(max)})`} htmlFor="unpick-quantity">
          <input
            id="unpick-quantity"
            type="number"
            min="0"
            step="1"
            data-testid="unpick-quantity"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            disabled={isSaving}
            className={inputClass}
          />
        </Field>

        <FilterableSelect
          variant="form"
          testId="unpick-slot"
          label="A qué ubicación vuelve"
          value={slotId}
          onChange={setSlotId}
          options={options}
          placeholder="Seleccionar..."
          searchPlaceholder="Buscar por nombre o código..."
          disabled={isSaving}
        />
        <p className="text-xs text-text-muted -mt-2">
          Vuelve a quedar disponible ahí, y la reserva de esta orden se reactiva.
        </p>

        <Field
          label="Lote"
          htmlFor="unpick-lot"
          hint="Solo si el producto se controla por lote: el mismo con el que se pickeó."
        >
          <input
            id="unpick-lot"
            type="text"
            data-testid="unpick-lot"
            value={lot}
            onChange={(event) => setLot(event.target.value)}
            disabled={isSaving}
            className={inputClass}
          />
        </Field>

        <Field label="Motivo" htmlFor="unpick-reason" hint="Opcional. Queda en el kardex junto al movimiento.">
          <input
            id="unpick-reason"
            type="text"
            data-testid="unpick-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            disabled={isSaving}
            className={inputClass}
          />
        </Field>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          loading={isSaving}
          loadingLabel="Devolviendo..."
          disabled={Boolean(problem)}
          onClick={handleSave}
          data-testid="unpick-save"
        >
          {problem || 'Devolver a la ubicación'}
        </Button>
      </div>
    </Modal>
  );
};
